/**
 * Export CSV de la progression d'une classe.
 *
 * Règle non négociable n° 5 : le fichier ne contient que des codes d'îlot, des
 * chapitres et des chiffres de jeu. Aucun nom, aucune date de naissance, rien
 * qui permette de remonter à un élève.
 *
 * Séparateur point-virgule et BOM UTF-8 : c'est ce qu'attend le tableur d'un
 * poste de collège, souvent Excel en français.
 */
import type { Content } from '@neopolis/engine';

import { T } from '../textes-interface.js';
import type { ProgressionIlot } from './api-prof.js';

const echapper = (valeur: string): string =>
  /[";\n]/.test(valeur) ? `"${valeur.replace(/"/g, '""')}"` : valeur;

/** Durée en minutes, arrondie, pour une lecture humaine. */
const minutes = (secondes: number | null): string =>
  secondes === null ? '' : String(Math.round(secondes / 60));

export function progressionEnCsv(content: Content, ilots: readonly ProgressionIlot[]): string {
  const titreChapitre = (id: string | null): string =>
    content.chapitres.find((c) => c.id === id)?.titre ?? '';

  const titreEtape = (chapterId: string | null, stepIndex: number | null): string => {
    if (chapterId === null || stepIndex === null) return '';
    return content.chapitres.find((c) => c.id === chapterId)?.deroule[stepIndex]?.titre ?? '';
  };

  const entetes = [
    T.prof.colonneIlot,
    T.prof.colonneChapitre,
    T.prof.colonneEtape,
    ...content.jauges.map((jauge) => jauge.nom),
    T.prof.colonneSolution,
    `${T.prof.colonneTemps} (min)`,
  ];

  const lignes = ilots.map((ilot) => [
    ilot.islandCode,
    titreChapitre(ilot.chapterId),
    titreEtape(ilot.chapterId, ilot.stepIndex),
    ...content.jauges.map((jauge) => String(ilot.gauges?.[jauge.id] ?? '')),
    ilot.solutionDeployee ?? '',
    minutes(ilot.tempsPasseSecondes),
  ]);

  return [entetes, ...lignes].map((ligne) => ligne.map(echapper).join(';')).join('\n');
}

/** Déclenche le téléchargement du CSV, avec le BOM attendu par les tableurs. */
export function telechargerCsv(nomFichier: string, csv: string): void {
  // \uFEFF : la marque d'ordre des octets, sans laquelle Excel en français
  // affiche les accents de travers. Écrite en échappement, un caractère
  // invisible dans le source étant illisible pour la personne qui relit.
  const fichier = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const lien = document.createElement('a');
  lien.href = URL.createObjectURL(fichier);
  lien.download = nomFichier;
  lien.click();
  URL.revokeObjectURL(lien.href);
}
